/**
 * Resolve once the DOM is parsed. Resolves immediately if it already is.
 *
 * @returns {Promise<void>}
 */
export async function domReady() {
    return new Promise((resolve) => {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => resolve())
        } else {
            resolve()
        }
    })
}

export const dom = (selector) => findOne(selector, document)
export const findOne = (selector, element) => (element || document).querySelector(selector)
export const findAll = (selector, element) => (element || document).querySelectorAll(selector)

/**
 * Like `findAll`, but also includes `element` itself when it matches `selector`.
 *
 * `App.mount(container)` scans `container` with each listener's selector, and a
 * descendants-only query would miss the container itself — yet a Turbo Stream
 * `replace`/`update` hands `App.mount` the target node ITSELF (and a listener's
 * selector can match a mounted turbo-frame). Self-matching keeps those covered.
 *
 * @returns {Element[]}
 */
export const findAllSelf = (selector, element) => {
    const root = element || document
    const found = [...root.querySelectorAll(selector)]
    if (root.nodeType === Node.ELEMENT_NODE && root.matches(selector)) {
        found.unshift(root)
    }
    return found
}

export const data = (element, name, value) => {
    // Getter
    if (typeof value === 'undefined') {
        return element.dataset[name]
    }

    element.dataset[name] = value
}

/**
 * Read a `data-*` attribute as a boolean, restoring the coercion jQuery's
 * `.data()` did implicitly. `data(el, 'x')` returns the string `"true"`, so
 * call sites that compared the value against `true` need this instead.
 *
 * Treats the same tokens jQuery coerced to truthy (`"true"`, `"1"`) as `true`;
 * everything else (including a missing attribute) is `false`. `name` is
 * camelCase like `data()` (e.g. `bannerCarousel` for `data-banner-carousel`).
 *
 * @returns {boolean}
 */
export const dataAsBool = (element, name) => {
    const value = data(element, name)
    return value === 'true' || value === '1'
}

/**
 * Show / hide an element via the `.hidden` utility class
 * (`display: none !important`, defined in scss/components/_helpers.scss).
 * Prefer these over toggling inline `style.display`, which CSS can't override.
 */
export const show = (element) => element.classList.remove('hidden')
export const hide = (element) => element.classList.add('hidden')

/**
 * Toggle an element's visibility. Omit `shouldShow` to flip the current state,
 * or pass a boolean to force shown (`true`) / hidden (`false`).
 */
export const toggle = (element, shouldShow) => {
    if (shouldShow === undefined) {
        element.classList.toggle('hidden')
        return
    }

    element.classList.toggle('hidden', !shouldShow)
}

export const on = (element, event, handler, useCapture) => {
    event.split(' ').forEach((event) => {
        element.addEventListener(event, handler, useCapture)
    })
}
export const trigger = (element, eventName, params) => {
    const event = new CustomEvent(eventName, {
        cancelable: true,
        detail: params,
    })
    element.dispatchEvent(event)
    return event
}

export const remove = (element) => {
    element.remove()
}

export const appendHTML = (element, html) => {
    const child = document.createElement('div')
    child.innerHTML = html

    while (child.firstChild) {
        element.appendChild(child.firstChild)
    }
}
